/** @jsx jsx */
import { gql } from '@apollo/client';
import { SemiTitle, Text } from '@bottlebooks/gatsby-design-system';
import { Col, Container, Link, Row } from '@bottlebooks/gatsby-theme-base';
import { Trans } from '@lingui/macro';
import { graphql } from 'gatsby';
import { jsx } from 'theme-ui';
import { Map, Marker } from '../Map/Map';
import BrandSocialMedia, { BrandSocialMediaFragment } from './BrandSocialMedia';

export default function BrandAddressSection({ brand, ...rest }) {
  if (!brand) return null;
  const {
    street,
    postalCode,
    city,
    telephone,
    email,
    website,
    name,
    latitude,
    longitude,
  } = brand;

  const streetLines = street?.split('\n') || [];
  const shortAddress = [...streetLines, `${postalCode || ''} ${city || ''}`]
    .filter(Boolean)
    .join(', ')
    .trim();
  // Normalizes URLs by adding http:// in front.
  const websiteUrl =
    website && `http://${website.trim()}`.replace(/^http:\/\/http/, 'http');

  return (
    <Container fluid sx={{ backgroundColor: 'light', marginY: 5 }} {...rest}>
      <Row>
        <Col sx={{ padding: 0, width: ['100%', '50%'], bg: 'hover' }}>
          {latitude != null && longitude != null && (
            <Map
              initialCenter={{ lat: latitude, lng: longitude }}
              zoom={13}
              sx={{ minHeight: ['50vw', '25vw'] }}
            >
              <Marker
                // @ts-ignore
                title={name}
                position={{ lat: latitude, lng: longitude }}
              />
            </Map>
          )}
        </Col>
        <Col
          sx={{
            paddingX: [null, `${100 / 12}%`],
            width: ['100%', '50%'],
            paddingTop: 4,
            paddingBottom: 5,
            alignSelf: 'center',
          }}
        >
          <SemiTitle>
            <Trans>Contact</Trans>
          </SemiTitle>
          <AddressSegment>{shortAddress}</AddressSegment>
          <AddressSegment>{telephone}</AddressSegment>
          <AddressSegment>
            {email && (
              <TouchableLink href={`mailto:${email}`}>{email}</TouchableLink>
            )}
          </AddressSegment>
          <AddressSegment>
            {website && (
              <TouchableLink
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {website}
              </TouchableLink>
            )}
          </AddressSegment>
          <BrandSocialMedia brand={brand} />
        </Col>
      </Row>
    </Container>
  );
}

function TouchableLink(props) {
  // Returns a link with enough padding to be touched with a finger (at least ~40px).
  return (
    // eslint-disable-next-line jsx-a11y/anchor-has-content
    <Link sx={{ padding: 1, margin: -1, display: 'inline-block' }} {...props} />
  );
}

function AddressSegment({ children }) {
  if (!children) return null;
  return (
    <div sx={{ display: 'flex', paddingBottom: 2 }}>
      <Text>{children}</Text>
    </div>
  );
}

export const fragment = graphql`
  fragment bb_BrandAddressSection on Bottlebooks_AbstractBrand {
    street
    postalCode
    city
    telephone
    email
    website
    name
    latitude
    longitude
    ...bb_BrandSocialMedia
  }
`;

export const BrandAddressSectionFragment = gql`
  ${BrandSocialMediaFragment}
  fragment BrandAddressSection on AbstractBrand {
    street
    postalCode
    city
    telephone
    email
    website
    name
    latitude
    longitude
    ...BrandSocialMedia
  }
`;
